import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  timeZoneShortLabel,
  utcToWallClock,
  wallClockToUtcIso,
  type TimeZonePref
} from "../../lib/datetime.js";

// Маленькое окно по центру экрана «Отправить позже» — как в оригинальном
// Telegram (ПКМ по кнопке отправки → Отправить позже). Дата и время в поясе
// саппорта (настройка «Часовой пояс»), наружу уходит абсолютный UTC ISO.
// То же окно используется для переноса времени уже отложенного сообщения.

export interface SchedulePickerResult {
  sendAt?: string;
  whenOnline?: boolean;
}

interface SchedulePickerModalProps {
  title?: string;
  submitLabel?: string;
  // Начальное значение (ISO). Нет — «сейчас + 1 час», округлённые до 5 минут.
  initialAt?: string | undefined;
  timeZone: TimeZonePref;
  // «Отправить, когда будет в сети» — только личные чаты.
  allowWhenOnline?: boolean;
  onSubmit(result: SchedulePickerResult): void;
  onClose(): void;
}

const pad = (value: number): string => value.toString().padStart(2, "0");

const defaultInitialMs = (): number => {
  const base = Date.now() + 60 * 60_000;
  const step = 5 * 60_000;
  return Math.ceil(base / step) * step;
};

const splitWall = (wall: string): { date: string; hh: string; mm: string } => {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(wall);
  return {
    date: match?.[1] ?? "",
    hh: match?.[2] ?? "00",
    mm: match?.[3] ?? "00"
  };
};

export const SchedulePickerModal = ({
  title = "Отправить позже",
  submitLabel = "Запланировать",
  initialAt,
  timeZone,
  allowWhenOnline = false,
  onSubmit,
  onClose
}: SchedulePickerModalProps) => {
  const initialWall = useMemo(
    () => utcToWallClock(initialAt ? new Date(initialAt) : defaultInitialMs(), timeZone),
    [initialAt, timeZone]
  );
  const initialParts = splitWall(initialWall);
  const [date, setDate] = useState(initialParts.date);
  const [hh, setHh] = useState(initialParts.hh);
  const [mm, setMm] = useState(initialParts.mm);
  // Промежуточный ввод времени (пока не валиден) — поле подсвечивается.
  const [timeInput, setTimeInput] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timeRef = useRef<HTMLInputElement | null>(null);

  const zoneLabel = timeZoneShortLabel(timeZone);
  const todayWall = utcToWallClock(Date.now(), timeZone).slice(0, 10);

  const resolvedIso = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return null;
    }
    return wallClockToUtcIso(`${date}T${hh}:${mm}`, timeZone);
  }, [date, hh, mm, timeZone]);

  const isFuture = resolvedIso ? new Date(resolvedIso).getTime() > Date.now() + 60_000 : false;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      timeRef.current?.focus();
      timeRef.current?.select();
    }, 30);
    return () => window.clearTimeout(handle);
  }, []);

  useEffect(() => {
    // Esc закрывает только окно (capture + stopPropagation): чат под ним
    // от Esc закрываться не должен.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const submit = (): void => {
    if (!resolvedIso) {
      setError("Укажите дату.");
      return;
    }
    if (!isFuture) {
      setError("Время должно быть в будущем — хотя бы через минуту.");
      return;
    }
    onSubmit({ sendAt: resolvedIso });
  };

  const applyPreset = (ms: number): void => {
    const parts = splitWall(utcToWallClock(ms, timeZone));
    setDate(parts.date);
    setHh(parts.hh);
    setMm(parts.mm);
    setTimeInput(null);
    setError(null);
  };

  const humanPreview =
    date && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? `${date.split("-").reverse().join(".")} ${hh}:${mm} ${zoneLabel}`
      : "";

  const nextDayAt = (hour: number, daysAhead: number): number => {
    const wall = utcToWallClock(Date.now() + daysAhead * 86_400_000, timeZone).slice(0, 10);
    const iso = wallClockToUtcIso(`${wall}T${pad(hour)}:00`, timeZone);
    return iso ? new Date(iso).getTime() : Date.now() + 60 * 60_000;
  };

  return createPortal(
    <div className="forward-modal" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        className="forward-modal__backdrop"
        onClick={onClose}
        aria-label="Отмена"
      />
      <form
        className="forward-modal__panel schedule-modal"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="forward-sheet__header">
          <div>
            <strong>{title}</strong>
            <p>Время указывается в вашем поясе ({zoneLabel}).</p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <i className="ic ic-close" aria-hidden="true" />
          </button>
        </div>

        <div className="schedule-modal__presets">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => applyPreset(Date.now() + 60 * 60_000)}
          >
            Через час
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => applyPreset(nextDayAt(20, 0))}
          >
            Сегодня в 20:00
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => applyPreset(nextDayAt(9, 1))}
          >
            Завтра в 9:00
          </button>
        </div>

        <div className="schedule-modal__fields">
          <label className="schedule-modal__field">
            <span>Дата</span>
            <input
              type="date"
              value={date}
              min={todayWall}
              onChange={(event) => {
                setDate(event.target.value);
                setError(null);
              }}
              aria-label={`Дата отправки (${zoneLabel})`}
            />
          </label>
          <label className="schedule-modal__field schedule-modal__field--time">
            <span>Время</span>
            <input
              ref={timeRef}
              type="text"
              inputMode="numeric"
              value={timeInput ?? `${hh}:${mm}`}
              onChange={(event) => {
                // Только цифры, максимум 4; двоеточие ставим сами — можно
                // печатать «1430» или «14:30».
                const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
                const masked =
                  digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
                const match = /^(\d{2}):(\d{2})$/.exec(masked);
                if (match) {
                  const nextHh = Number(match[1]);
                  const nextMm = Number(match[2]);
                  if (nextHh < 24 && nextMm < 60) {
                    setHh(pad(nextHh));
                    setMm(pad(nextMm));
                    setTimeInput(null);
                    setError(null);
                    return;
                  }
                }
                setTimeInput(masked);
              }}
              onBlur={() => setTimeInput(null)}
              onFocus={(event) => event.currentTarget.select()}
              placeholder="ЧЧ:ММ"
              maxLength={5}
              className={timeInput !== null ? "schedule-modal__time--invalid" : undefined}
              aria-label={`Время отправки (24 часа, ${zoneLabel})`}
            />
          </label>
        </div>

        <div className="schedule-modal__preview">
          {humanPreview ? (
            <>
              <i className="ic ic-schedule" aria-hidden="true" /> Отправится {humanPreview}
              {!isFuture ? <span className="schedule-modal__warn"> — это уже прошло</span> : null}
            </>
          ) : (
            "Выберите дату и время"
          )}
        </div>
        {error ? <div className="schedule-modal__error">{error}</div> : null}

        <div className="translator-modal__actions schedule-modal__actions">
          {allowWhenOnline ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => onSubmit({ whenOnline: true })}
              title="Telegram отправит сообщение, как только собеседник появится в сети"
            >
              Когда будет в сети
            </button>
          ) : null}
          <button type="submit" className="button" disabled={!resolvedIso}>
            <i className="ic ic-schedule" aria-hidden="true" /> {submitLabel}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};
